import {CommonModule} from '@angular/common';
import {
    AfterViewInit,
    Component,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    TemplateRef,
    ViewChild
} from '@angular/core';
import {
    AbstractControl,
    FormArray,
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import {MaterialModule} from '../../../material.module';
import {
    Webinar,
    WebinarAnalyticsResponse,
    WebinarChatModerationAction,
    WebinarChatMessage,
    WebinarEngagementInput,
    WebinarEngagementItem,
    WebinarEngagementType,
    WebinarPollResultsVisibility,
    WebinarRegistration,
    WebinarSeries,
} from '../../../models/webinar.model';
import {MatTableDataSource} from '@angular/material/table';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {MatDialog} from '@angular/material/dialog';
import {BreadcrumbsComponent} from '../../../shared/breadcrumbs/breadcrumbs.component';
import {linkifyWebinarChatText, WebinarChatTextPart} from './webinar-chat-linkifier';
import {WebinarAnalyticsPanelComponent} from '../webinar-analytics-panel/webinar-analytics-panel.component';

type WebinarChatReaction = NonNullable<WebinarChatMessage['reactions']>[string];
type EngagementDeliveryMode = 'draft' | 'scheduled';
type EngagementScheduleResolution = {
    scheduledFor: string | null;
    error: 'invalid' | 'ambiguous' | null;
};

@Component({
    selector: 'app-webinars-detail',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        MaterialModule,
        BreadcrumbsComponent,
        WebinarAnalyticsPanelComponent,
    ],
    templateUrl: './webinars-detail.component.html',
    styleUrls: ['./webinars-detail.component.scss'],
    host: {class: 'webinars-m3 webinars-shell'},
})
export class WebinarsDetailComponent implements OnChanges, AfterViewInit {
    private readonly dialog = inject(MatDialog);

    @Input() form!: FormGroup;
    @Input() selectedWebinar: Webinar | null = null;
    @Input() isSaving = false;
    @Input() registrations: WebinarRegistration[] = [];
    @Input() registrationColumns: string[] = [];
    @Input() chatMessages: WebinarChatMessage[] = [];
    @Input() pinnedChatMessageId: string | null = null;
    @Input() isChatLoading = false;
    @Input() analytics: WebinarAnalyticsResponse | null = null;
    @Input() isAnalyticsLoading = false;
    @Input() analyticsErrorMessage: string | null = null;
    @Input() engagementItems: WebinarEngagementItem[] = [];
    @Input() isEngagementLoading = false;
    @Input() hasHostPassword = false;
    @Input() hostPasswordMasked = false;
    @Input() recurrencePreviewOccurrences: Webinar[] = [];
    @Input() recurrencePreviewSeries: WebinarSeries | null = null;
    @Input() isRecurrencePreviewLoading = false;
    @Input() recurrencePreviewError: string | null = null;
    hostPasswordVisible = false;

    // Chats: show separate Date and Time columns (time uses the original ts)
    readonly chatDisplayedColumns = ['user', 'date', 'ts', 'text', 'reactions', 'moderation'];

    // DataSources and filtering for tables to match the list view experience
    registrationDataSource = new MatTableDataSource<WebinarRegistration>([]);
    chatTableData: WebinarChatMessage[] = [];
    private chatFiltered: WebinarChatMessage[] = [];
    chatPageSize = 10;
    chatPageIndex = 0;
    chatLength = 0;
    private userDisplayMap = new Map<string, string>();

    registrationFilterTerm = '';
    chatFilterTerm = '';

    // Note: paginators live inside lazy mat-tab content. Use setter-based ViewChild
    // so they bind when the tab content renders, not only during ngAfterViewInit.
    private _registrationPaginator?: MatPaginator;
    private _chatPaginator?: MatPaginator;
    @ViewChild('deleteConfirmDialog') deleteConfirmDialog?: TemplateRef<any>;

    @ViewChild('registrationPaginator')
    set registrationPaginator(p: MatPaginator | undefined) {
        this._registrationPaginator = p;
        if (p) {
            this.registrationDataSource.paginator = p;
            p.pageSize = 10;
            // Ensure we reset to first page when attaching
            try { p.firstPage(); } catch {}
        }
    }

    get registrationPaginator(): MatPaginator | undefined {
        return this._registrationPaginator;
    }

    @ViewChild('chatPaginator', {static: false}) chatPaginator?: MatPaginator;

    @Output() save = new EventEmitter<void>();
    @Output() exportRegistrations = new EventEmitter<void>();
    @Output() exportChats = new EventEmitter<void>();
    @Output() delete = new EventEmitter<void>();
    @Output() refresh = new EventEmitter<void>();
    @Output() openPreview = new EventEmitter<void>();
    @Output() refreshAnalytics = new EventEmitter<void>();
    @Output() createEngagement = new EventEmitter<WebinarEngagementInput>();
    @Output() updateEngagement = new EventEmitter<{ id: string; input: WebinarEngagementInput }>();
    @Output() deleteEngagement = new EventEmitter<string>();
    @Output() publishEngagement = new EventEmitter<string>();
    @Output() closeEngagement = new EventEmitter<string>();
    @Output() refreshEngagements = new EventEmitter<void>();
    @Output() moderateChat = new EventEmitter<{ message: WebinarChatMessage; action: WebinarChatModerationAction }>();
    @Output() previewSeries = new EventEmitter<void>();

    // Plain string for header label to avoid complex template expressions
    public lastUpdatedLabel = '';
    deleteConfirmValue = '';

    engagementType: WebinarEngagementType = 'poll';
    engagementDeliveryMode: EngagementDeliveryMode = 'draft';
    engagementEditId: string | null = null;
    engagementMessageText = '';
    engagementQuestion = '';
    engagementAllowMultiple = false;
    engagementResultsVisibility: WebinarPollResultsVisibility = 'attendees';
    engagementShowResponseCount = true;
    engagementOptions: Array<{ label: string; leadOnSelect: boolean }> = [
        {label: '', leadOnSelect: false},
        {label: '', leadOnSelect: false},
    ];
    engagementResourceTitle = '';
    engagementResourceUrl = '';
    engagementResourceDescription = '';
    engagementResourceFileType = '';
    engagementResourceButtonLabel = '';
    engagementScheduleDate = '';
    engagementScheduleTime = '';
    engagementScheduleTimezone = this.defaultEngagementTimezone();
    readonly maxMessageLength = 1000;
    readonly maxPollOptions = 8;
    readonly maxSpeakerMembers = 50;
    readonly weekdayOptions = [
        {value: 'MO', label: 'Mon'},
        {value: 'TU', label: 'Tue'},
        {value: 'WE', label: 'Wed'},
        {value: 'TH', label: 'Thu'},
        {value: 'FR', label: 'Fri'},
        {value: 'SA', label: 'Sat'},
        {value: 'SU', label: 'Sun'},
    ];

    get breadcrumbs(): Array<{label: string; route?: string}> {
        const title = this.selectedWebinar?.title || 'Create webinar';
        return [
            {label: 'Shine Webinars', route: '/webinars'},
            {label: 'Webinars', route: '/webinars'},
            {label: title},
        ];
    }

    get isEditingEngagement(): boolean {
        return Boolean(this.engagementEditId);
    }

    get engagementList(): WebinarEngagementItem[] {
        const items = Array.isArray(this.engagementItems) ? this.engagementItems.slice() : [];
        return items.sort((a, b) => this.getEngagementSortDate(b) - this.getEngagementSortDate(a));
    }

    get engagementFormTitle(): string {
        return this.isEditingEngagement ? 'Edit engagement' : 'New engagement';
    }

    get engagementSubmitLabel(): string {
        if (this.isEditingEngagement) {
            return this.engagementDeliveryMode === 'scheduled' ? 'Update scheduled item' : 'Update engagement';
        }
        return this.engagementDeliveryMode === 'scheduled' ? 'Create scheduled item' : 'Create engagement';
    }

    get hasValidPollOptions(): boolean {
        const labels = this.getTrimmedPollOptions().map((opt) => opt.label.toLowerCase());
        const unique = new Set(labels);
        return unique.size >= 2;
    }

    get canSubmitEngagement(): boolean {
        if (!this.selectedWebinar?.id || this.isWebinarEnded) return false;
        if (this.engagementType === 'message' && !this.engagementMessageText.trim()) return false;
        if (this.engagementType === 'poll' && !(Boolean(this.engagementQuestion.trim()) && this.hasValidPollOptions)) {
            return false;
        }
        if (this.engagementType === 'resource' && !(Boolean(this.engagementResourceTitle.trim()) && this.isValidHttpUrl(this.engagementResourceUrl))) {
            return false;
        }
        return this.engagementScheduleErrorMessage === null;
    }

    get isSchedulingEngagement(): boolean {
        return this.engagementDeliveryMode === 'scheduled';
    }

    get isWebinarEnded(): boolean {
        const endMs = Date.parse(String(this.selectedWebinar?.endAt || ''));
        return Number.isFinite(endMs) && endMs <= Date.now();
    }

    get engagementScheduleErrorMessage(): string | null {
        if (!this.isSchedulingEngagement) return null;

        const localDate = this.normaliseScheduleLocalDate(this.engagementScheduleDate);
        const localTime = this.normaliseScheduleLocalTime(this.engagementScheduleTime);
        const timezone = this.normaliseEngagementTimezone(this.engagementScheduleTimezone);

        if (!localDate || !localTime) {
            return 'Choose both a date and time.';
        }
        if (!timezone) {
            return 'Timezone is not available for this schedule.';
        }

        const schedule = this.resolveLocalDateTime(localDate, localTime, timezone);
        if (schedule.error === 'ambiguous') {
            return 'This local time occurs twice because of DST fallback. Choose a different time.';
        }
        if (!schedule.scheduledFor) {
            return 'Choose a valid local date and time.';
        }
        if (Date.parse(schedule.scheduledFor) <= Date.now()) {
            return 'Scheduled time must be in the future.';
        }

        const webinarEndMs = Date.parse(String(this.selectedWebinar?.endAt || ''));
        if (Number.isFinite(webinarEndMs) && Date.parse(schedule.scheduledFor) >= webinarEndMs) {
            return 'Scheduled time must be before the webinar end time.';
        }

        return null;
    }

    get engagementDeliverySummary(): string {
        if (!this.isSchedulingEngagement) {
            return 'Create a draft item and publish it manually when you are ready.';
        }
        return `Scheduled items use ${this.engagementScheduleTimezone}.`;
    }

    get speakerMembers(): AbstractControl[] {
        const speakers = this.form?.get('speakers');
        return speakers instanceof FormArray ? speakers.controls : [];
    }

    get canAddSpeaker(): boolean {
        return this.speakerMembers.length < this.maxSpeakerMembers;
    }

    get isRecurringCreate(): boolean {
        return !this.selectedWebinar && this.form?.get('webinarKind')?.value === 'recurring';
    }

    get isResourceUrlValid(): boolean {
        return this.isValidHttpUrl(this.engagementResourceUrl);
    }

    onSubmit(): void {
        if (this.form?.invalid) {
            this.form.markAllAsTouched();
            return;
        }
        this.save.emit();
    }

    addSpeaker(): void {
        const speakers = this.form?.get('speakers');
        if (!(speakers instanceof FormArray) || speakers.length >= this.maxSpeakerMembers) return;
        speakers.push(new FormGroup({
            email: new FormControl('', [Validators.required, Validators.email]),
            name: new FormControl('', [Validators.maxLength(64)]),
        }));
        speakers.markAsDirty();
        speakers.updateValueAndValidity();
    }

    removeSpeaker(index: number): void {
        const speakers = this.form?.get('speakers');
        if (!(speakers instanceof FormArray)) return;
        speakers.removeAt(index);
        speakers.markAsDirty();
        speakers.updateValueAndValidity();
    }

    openDeleteDialog(): void {
        this.deleteConfirmValue = '';
        if (this.deleteConfirmDialog) {
            this.dialog.open(this.deleteConfirmDialog, {width: '420px'});
        }
    }

    protected getReactionCount(reaction: unknown): number {
        if (!this.isWebinarChatReaction(reaction)) {
            return 0;
        }

        const userCount = reaction.users?.length ?? 0;
        return reaction.count ?? userCount;
    }

    moderateChatMessage(message: WebinarChatMessage, action: WebinarChatModerationAction): void {
        if (!message?.id || message.type !== 'chat') return;
        if (action === 'unpin' && !this.isChatMessagePinned(message)) return;
        this.moderateChat.emit({message, action});
    }

    protected isChatMessagePinned(message: WebinarChatMessage): boolean {
        return Boolean(message?.id && this.pinnedChatMessageId === message.id);
    }

    // Fallback date formatter to avoid template pipe parsing issues in some environments
    protected formatDateMedium(value: unknown): string {
        if (!value) return '';
        const d = new Date(value as any);
        if (isNaN(d.getTime())) return '';
        try {
            return d.toLocaleString();
        } catch {
            return d.toString();
        }
    }

    // Helpers to format date-only and time-only for table columns
    // Date format: dd.MM.yyyy (e.g., 05.12.2025)
    protected formatDateOnly(value: unknown): string {
        if (!value) return '';
        const d = new Date(value as any);
        if (isNaN(d.getTime())) return '';
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${dd}.${mm}.${yyyy}`;
    }

    protected formatTimeOnly(value: unknown): string {
        if (!value) return '';
        const d = new Date(value as any);
        if (isNaN(d.getTime())) return '';
        try {
            return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } catch {
            // Fallback to HH:MM from ISO if locale call fails
            const h = d.getHours().toString().padStart(2, '0');
            const m = d.getMinutes().toString().padStart(2, '0');
            return `${h}:${m}`;
        }
    }

    // Registrations: externally provided columns may include 'registeredAt'.
    // Map that to two internal columns: 'registeredDate' and 'registeredTime'
    get registrationDisplayedColumns(): string[] {
        return (this.registrationColumns || []).flatMap(col =>
            col === 'registeredAt' ? ['registeredDate', 'registeredTime'] : [col]
        );
    }

    private isWebinarChatReaction(reaction: unknown): reaction is WebinarChatReaction {
        if (reaction === null || typeof reaction !== 'object') {
            return false;
        }

        const candidate = reaction as Partial<WebinarChatReaction>;
        if (candidate.count !== undefined && typeof candidate.count !== 'number') {
            return false;
        }

        if (candidate.users !== undefined && !Array.isArray(candidate.users)) {
            return false;
        }

        return true;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['registrations']) {
            this.registrationDataSource.data = this.registrations ?? [];
            if (this.registrationPaginator) {
                this.registrationPaginator.firstPage();
            }
        }
        if (changes['chatMessages']) {
            this.rebuildUserDisplayMap();
            this.refreshChatData(true);
        }

        if (changes['selectedWebinar']) {
            const w = this.selectedWebinar;
            const dt = (w && (w.updatedAt || w.createdAt)) || '';
            this.lastUpdatedLabel = this.formatDateMedium(dt);
        }

        if (changes['engagementItems'] && this.engagementEditId) {
            const stillExists = (this.engagementItems || []).some((item) => item.id === this.engagementEditId);
            if (!stillExists) {
                this.resetEngagementForm();
            }
        }
    }

    ngAfterViewInit(): void {
        // Kept for safety if tabs are pre-rendered; setter ViewChilds handle lazy render
        if (this.registrationPaginator) {
            this.registrationDataSource.paginator = this.registrationPaginator;
            this.registrationPaginator.pageSize = 10;
        }
        if (this.chatPaginator) {
            this.chatPaginator.pageSize = this.chatPageSize;
            this.refreshChatData(true);
        }
    }

    onSearchRegistrations(event: Event): void {
        const value = (event.target as HTMLInputElement | null)?.value ?? '';
        this.registrationFilterTerm = value;
        // Simple client-side filter: search across name, email, source
        this.registrationDataSource.filterPredicate = (row) => {
            const hay = `${row.name ?? ''} ${row.email ?? ''} ${row.source ?? ''}`.toLowerCase();
            return hay.includes(value.trim().toLowerCase());
        };
        this.registrationDataSource.filter = value.trim().toLowerCase();
        if (this.registrationPaginator) this.registrationPaginator.firstPage();
    }

    onClearRegistrationSearch(): void {
        this.registrationFilterTerm = '';
        this.registrationDataSource.filter = '';
        if (this.registrationPaginator) this.registrationPaginator.firstPage();
    }

    onSearchChats(event: Event): void {
        const value = (event.target as HTMLInputElement | null)?.value ?? '';
        this.chatFilterTerm = value;
        this.refreshChatData(true);
    }

    onClearChatSearch(): void {
        this.chatFilterTerm = '';
        this.refreshChatData(true);
    }

    onChatPage(event: PageEvent): void {
        this.chatPageIndex = event.pageIndex;
        this.chatPageSize = event.pageSize;
        this.refreshChatData(false);
    }

    private refreshChatData(resetPage: boolean): void {
        const term = (this.chatFilterTerm || '').trim().toLowerCase();
        const source = this.chatMessages ?? [];

        // Refresh user display map when source changes
        if (resetPage) {
            this.rebuildUserDisplayMap();
        }

        this.chatFiltered = term
            ? source.filter((row) => {
                  const user = row.user?.name || row.user?.id || '';
                  const reply = row.replyTo?.text || '';
                  const tsStr = new Date(row.ts).toLocaleString();
                  const hay = `${user} ${row.text ?? ''} ${reply} ${tsStr}`.toLowerCase();
                  return hay.includes(term);
              })
            : source.slice();

        if (resetPage) {
            this.chatPageIndex = 0;
        }

        this.chatLength = this.chatFiltered.length;

        const totalPages = Math.max(1, Math.ceil(Math.max(this.chatLength, 1) / this.chatPageSize));
        if (this.chatPageIndex >= totalPages) {
            this.chatPageIndex = totalPages - 1;
        }
        if (this.chatPageIndex < 0) this.chatPageIndex = 0;

        const start = this.chatPageIndex * this.chatPageSize;
        const end = start + this.chatPageSize;
        this.chatTableData = this.chatFiltered.slice(start, end);

        if (this.chatPaginator) {
            this.chatPaginator.length = this.chatLength;
            this.chatPaginator.pageIndex = this.chatPageIndex;
            this.chatPaginator.pageSize = this.chatPageSize;
        }
    }

    private rebuildUserDisplayMap(): void {
        this.userDisplayMap.clear();
        (this.chatMessages ?? []).forEach((msg) => {
            const id = msg.user?.id;
            const name = msg.user?.name;
            if (id && name) {
                this.userDisplayMap.set(id, name);
            }
        });
    }

    private isUuid(value: string): boolean {
        return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
    }

    formatReactionUsers(reaction: unknown): string {
        const users = (reaction as any)?.users as any;
        if (!Array.isArray(users) || !users.length) return '';
        const names = users
            .map((u) => {
                if (u == null) return '';
                if (typeof u === 'string') {
                    const mapped = this.userDisplayMap.get(u);
                    if (mapped) return mapped;
                    if (this.isUuid(u)) return '';
                    const lower = u.toLowerCase();
                    if (lower.includes('@')) return '';
                    return u;
                }
                if (typeof u === 'object') {
                    const obj = u as any;
                    const objName = obj.name || obj.id;
                    if (!objName) return '';
                    if (typeof objName === 'string' && this.userDisplayMap.has(objName)) {
                        return this.userDisplayMap.get(objName) ?? '';
                    }
                    return objName;
                }
                return '';
            })
            .filter((s) => !!s && typeof s === 'string');
        return names.length ? names.join(', ') : `${users.length} participant${users.length === 1 ? '' : 's'}`;
    }

    formatReactionUsersSafe(reaction: unknown): string {
        const txt = this.formatReactionUsers(reaction);
        return typeof txt === 'string' ? txt : '';
    }

    protected getChatTextParts(value: unknown): WebinarChatTextPart[] {
        return linkifyWebinarChatText(value);
    }

    onEngagementTypeChange(type: WebinarEngagementType): void {
        if (this.isEditingEngagement) return;
        this.engagementType = type;
        this.resetEngagementDetails();
    }

    onEngagementDeliveryModeChange(mode: EngagementDeliveryMode): void {
        this.engagementDeliveryMode = mode;
        if (mode !== 'scheduled') {
            this.engagementScheduleDate = '';
            this.engagementScheduleTime = '';
            if (!this.isEditingEngagement) {
                this.engagementScheduleTimezone = this.defaultEngagementTimezone();
            }
        }
    }

    addPollOption(): void {
        if (this.engagementOptions.length >= this.maxPollOptions) return;
        this.engagementOptions.push({label: '', leadOnSelect: false});
    }

    removePollOption(index: number): void {
        if (this.engagementOptions.length <= 2) return;
        this.engagementOptions.splice(index, 1);
    }

    submitEngagement(): void {
        if (!this.canSubmitEngagement) return;
        const input = this.buildEngagementInput();
        if (!input) return;
        if (this.engagementEditId) {
            this.updateEngagement.emit({id: this.engagementEditId, input});
        } else {
            this.createEngagement.emit(input);
        }
    }

    resetEngagementForm(): void {
        this.engagementEditId = null;
        this.resetEngagementDetails();
    }

    startEditEngagement(item: WebinarEngagementItem): void {
        if (!this.canEditEngagement(item)) return;
        this.engagementEditId = item.id;
        this.engagementType = item.type;
        this.engagementDeliveryMode = item.status === 'scheduled' ? 'scheduled' : 'draft';
        this.engagementScheduleTimezone = this.normaliseEngagementTimezone(item.timezone) || this.defaultEngagementTimezone();
        this.engagementScheduleDate = item.scheduleLocalDate || this.formatScheduleLocalDate(item.scheduledFor) || '';
        this.engagementScheduleTime = item.scheduleLocalTime || this.formatScheduleLocalTime(item.scheduledFor) || '';
        if (item.type === 'message') {
            this.engagementMessageText = item.message?.text || '';
        } else if (item.type === 'poll') {
            const poll = item.poll || {
                question: '',
                allowMultiple: false,
                resultsVisibility: 'attendees',
                showResponseCount: true,
                options: [],
            };
            this.engagementQuestion = poll.question || '';
            this.engagementAllowMultiple = Boolean(poll.allowMultiple);
            this.engagementResultsVisibility = poll.resultsVisibility || 'attendees';
            this.engagementShowResponseCount = poll.showResponseCount !== false;
            this.engagementOptions = (poll.options || []).map((opt) => ({
                label: opt.label || '',
                leadOnSelect: Boolean(opt.leadOnSelect),
            }));
            while (this.engagementOptions.length < 2) {
                this.engagementOptions.push({label: '', leadOnSelect: false});
            }
        } else if (item.type === 'resource') {
            const resource = item.resource || {title: '', url: ''};
            this.engagementResourceTitle = resource.title || '';
            this.engagementResourceUrl = resource.url || '';
            this.engagementResourceDescription = resource.description || '';
            this.engagementResourceFileType = resource.fileType || '';
            this.engagementResourceButtonLabel = resource.buttonLabel || '';
        }
    }

    confirmDeleteEngagement(item: WebinarEngagementItem): void {
        if (!this.canDeleteEngagement(item)) return;
        const confirmed = window.confirm('Delete this engagement item? This cannot be undone.');
        if (confirmed) {
            this.deleteEngagement.emit(item.id);
        }
    }

    publishSelectedEngagement(item: WebinarEngagementItem): void {
        if (!this.canPublishEngagement(item)) return;
        this.publishEngagement.emit(item.id);
    }

    cancelScheduledEngagement(item: WebinarEngagementItem): void {
        if (!this.canCancelEngagement(item)) return;
        this.updateEngagement.emit({
            id: item.id,
            input: {status: 'cancelled'},
        });
        if (this.engagementEditId === item.id) {
            this.resetEngagementForm();
        }
    }

    closeSelectedEngagement(item: WebinarEngagementItem): void {
        if (!item || item.type !== 'poll' || item.status !== 'posted') return;
        this.closeEngagement.emit(item.id);
    }

    formatEngagementType(item: WebinarEngagementItem): string {
        if (item.type === 'message') return 'Message';
        if (item.type === 'poll') return 'Poll';
        if (item.type === 'resource') return 'Tracked link';
        return 'Engagement';
    }

    formatEngagementStatus(status: WebinarEngagementItem['status']): string {
        switch (status) {
            case 'scheduled':
                return 'Scheduled';
            case 'posting':
                return 'Posting';
            case 'posted':
                return 'Posted';
            case 'closed':
                return 'Closed';
            case 'cancelled':
                return 'Cancelled';
            case 'expired':
                return 'Expired';
            case 'draft':
            default:
                return 'Draft';
        }
    }

    formatEngagementTitle(item: WebinarEngagementItem): string {
        if (item.type === 'message') {
            return this.truncateEngagementTitle(item.message?.text || 'Message');
        }
        if (item.type === 'poll') return item.poll?.question || 'Poll';
        if (item.type === 'resource') return item.resource?.title || 'Tracked link';
        return 'Engagement';
    }

    formatEngagementSchedule(item: WebinarEngagementItem): string {
        if (item.scheduleLocalDate && item.scheduleLocalTime) {
            const timezone = this.normaliseEngagementTimezone(item.timezone);
            return timezone
                ? `${item.scheduleLocalDate} ${item.scheduleLocalTime} (${timezone})`
                : `${item.scheduleLocalDate} ${item.scheduleLocalTime}`;
        }
        if (item.scheduledFor) {
            return this.formatDateMedium(item.scheduledFor);
        }
        return 'Not scheduled';
    }

    getEngagementStateNote(item: WebinarEngagementItem): string | null {
        if (!item) return null;
        if (item.status === 'posting') {
            return 'Runtime is dispatching this item.';
        }
        if (item.status === 'scheduled' && this.isScheduledEngagementOverdue(item)) {
            return this.isWebinarEnded
                ? 'The webinar has ended. Runtime will expire this scheduled item on the next dispatcher pass.'
                : 'Due time has passed. Runtime dispatch is expected to post this item shortly.';
        }
        if (item.status === 'expired') {
            return item.expiredReason === 'webinar_ended'
                ? 'This item expired because the webinar ended before it could be posted.'
                : 'This scheduled item expired before it could be posted.';
        }
        if (item.status === 'cancelled') {
            return 'This schedule was cancelled before publish.';
        }
        if (this.isWebinarEnded && this.isMutableEngagement(item)) {
            return 'This webinar has ended, so draft and scheduled items are read-only.';
        }
        return null;
    }

    formatPollResultsVisibility(value: WebinarPollResultsVisibility | undefined): string {
        return value === 'host' ? 'Host only' : 'Attendees + host';
    }

    formatPollResponseCountVisibility(showResponseCount: boolean | undefined): string {
        return showResponseCount === false
            ? 'Response counts hidden from attendees'
            : 'Response counts visible to attendees';
    }

    getPollOptionCount(item: WebinarEngagementItem, optionId: string | undefined): number {
        if (!optionId || !item.stats?.optionCounts) return 0;
        return Number(item.stats.optionCounts[String(optionId)] ?? 0);
    }

    getHubspotLeadSyncSummary(item: WebinarEngagementItem): string {
        const sync = item.hubspotLeadSync;
        if (!sync || sync.expectedCount <= 0) return '';
        if (!sync.hasInconsistencies) {
            return `${sync.completedCount}/${sync.expectedCount} HubSpot leads created`;
        }
        return `${sync.inconsistentCount} of ${sync.expectedCount} HubSpot leads need attention`;
    }

    getHubspotLeadSyncStatus(item: WebinarEngagementItem, responseId: string | undefined | null): string | null {
        if (!responseId || !item.hubspotLeadSync?.responses?.length) return null;
        const entry = item.hubspotLeadSync.responses.find((candidate) => candidate.responseId === responseId);
        return entry?.status || null;
    }

    formatHubspotLeadSyncStatus(status: string | null): string {
        switch (status) {
            case 'completed':
                return 'HubSpot lead created';
            case 'failed':
                return 'HubSpot lead failed';
            case 'missing_outbox':
                return 'HubSpot lead missing';
            case 'processing':
                return 'HubSpot lead processing';
            case 'pending':
                return 'HubSpot lead pending';
            default:
                return '';
        }
    }

    formatPollResponseOptions(item: WebinarEngagementItem, optionIds: string[]): string {
        const options = Array.isArray(item.poll?.options) ? item.poll?.options : [];
        const map = new Map(options.map((opt) => [String(opt.id), opt.label]));
        const labels = optionIds
            .map((id) => map.get(String(id)) || String(id))
            .filter((label) => label && typeof label === 'string');
        return labels.length ? labels.join(', ') : 'Unknown option';
    }

    formatEngagementPersonLabel(entry: {
        name?: string | null;
        email?: string | null;
        userId?: string | null
    }): string {
        const name = entry.name ? entry.name.trim() : '';
        if (name) return name;
        const email = entry.email ? entry.email.trim() : '';
        if (email) return email;
        const userId = entry.userId ? entry.userId.trim() : '';
        return userId || 'Anonymous';
    }

    private resetEngagementDetails(): void {
        this.engagementDeliveryMode = 'draft';
        this.engagementMessageText = '';
        this.engagementQuestion = '';
        this.engagementAllowMultiple = false;
        this.engagementResultsVisibility = 'attendees';
        this.engagementShowResponseCount = true;
        this.engagementOptions = [
            {label: '', leadOnSelect: false},
            {label: '', leadOnSelect: false},
        ];
        this.engagementResourceTitle = '';
        this.engagementResourceUrl = '';
        this.engagementResourceDescription = '';
        this.engagementResourceFileType = '';
        this.engagementResourceButtonLabel = '';
        this.engagementScheduleDate = '';
        this.engagementScheduleTime = '';
        this.engagementScheduleTimezone = this.defaultEngagementTimezone();
    }

    private buildEngagementInput(): WebinarEngagementInput | null {
        const scheduleInput = this.buildEngagementScheduleInput();
        if (!scheduleInput) return null;

        if (this.engagementType === 'message') {
            return {
                type: 'message',
                text: this.engagementMessageText.trim(),
                ...scheduleInput,
            };
        }
        if (this.engagementType === 'poll') {
            return {
                type: 'poll',
                question: this.engagementQuestion.trim(),
                allowMultiple: this.engagementAllowMultiple,
                resultsVisibility: this.engagementResultsVisibility,
                showResponseCount: this.engagementShowResponseCount,
                options: this.getTrimmedPollOptions(),
                ...scheduleInput,
            };
        }
        if (this.engagementType === 'resource') {
            return {
                type: 'resource',
                title: this.engagementResourceTitle.trim(),
                url: this.engagementResourceUrl.trim(),
                description: this.engagementResourceDescription.trim() || null,
                fileType: this.engagementResourceFileType.trim() || null,
                buttonLabel: this.engagementResourceButtonLabel.trim() || null,
                ...scheduleInput,
            };
        }
        return scheduleInput;
    }

    private getTrimmedPollOptions(): Array<{ label: string; leadOnSelect?: boolean }> {
        const trimmed = this.engagementOptions
            .map((opt) => ({label: String(opt.label || '').trim(), leadOnSelect: Boolean(opt.leadOnSelect)}))
            .filter((opt) => opt.label.length > 0);
        const seen = new Set<string>();
        const unique: Array<{ label: string; leadOnSelect?: boolean }> = [];
        trimmed.forEach((opt) => {
            const key = opt.label.toLowerCase();
            if (seen.has(key)) return;
            seen.add(key);
            unique.push(opt);
        });
        return unique;
    }

    private isValidHttpUrl(value: string): boolean {
        const raw = String(value || '').trim();
        if (!raw) return false;
        try {
            const parsed = new URL(raw);
            return parsed.protocol === 'http:' || parsed.protocol === 'https:';
        } catch {
            return false;
        }
    }

    private getEngagementSortDate(item: WebinarEngagementItem): number {
        const scheduledFor = Date.parse(String(item?.scheduledFor || ''));
        if (Number.isFinite(scheduledFor) && item?.status === 'scheduled') return scheduledFor;
        const postedAt = Date.parse(String(item?.postedAt || ''));
        if (Number.isFinite(postedAt)) return postedAt;
        const updatedAt = Date.parse(String(item?.updatedAt || ''));
        if (Number.isFinite(updatedAt)) return updatedAt;
        const createdAt = Date.parse(String(item?.createdAt || ''));
        if (Number.isFinite(createdAt)) return createdAt;
        return 0;
    }

    private buildEngagementScheduleInput(): WebinarEngagementInput | null {
        if (!this.isSchedulingEngagement) {
            return {status: 'draft'};
        }

        const error = this.engagementScheduleErrorMessage;
        if (error) {
            return null;
        }

        const scheduleLocalDate = this.normaliseScheduleLocalDate(this.engagementScheduleDate);
        const scheduleLocalTime = this.normaliseScheduleLocalTime(this.engagementScheduleTime);
        const timezone = this.normaliseEngagementTimezone(this.engagementScheduleTimezone);
        if (!scheduleLocalDate || !scheduleLocalTime || !timezone) {
            return null;
        }

        const schedule = this.resolveLocalDateTime(scheduleLocalDate, scheduleLocalTime, timezone);
        if (!schedule.scheduledFor) {
            return null;
        }

        return {
            status: 'scheduled',
            scheduledFor: schedule.scheduledFor,
            scheduleLocalDate,
            scheduleLocalTime,
            timezone,
        };
    }

    private isMutableEngagement(item: WebinarEngagementItem | null | undefined): boolean {
        return item?.status === 'draft' || item?.status === 'scheduled';
    }

    canEditEngagement(item: WebinarEngagementItem | null | undefined): boolean {
        return Boolean(item && this.isMutableEngagement(item) && !this.isWebinarEnded);
    }

    canDeleteEngagement(item: WebinarEngagementItem | null | undefined): boolean {
        return Boolean(item && item.status === 'draft' && !this.isWebinarEnded);
    }

    canCancelEngagement(item: WebinarEngagementItem | null | undefined): boolean {
        return Boolean(item && item.status === 'scheduled' && !this.isWebinarEnded);
    }

    canPublishEngagement(item: WebinarEngagementItem | null | undefined): boolean {
        if (!item) return false;
        if (item.status === 'draft' || item.status === 'scheduled') return !this.isWebinarEnded;
        return false;
    }

    private isScheduledEngagementOverdue(item: WebinarEngagementItem | null | undefined): boolean {
        const scheduledMs = Date.parse(String(item?.scheduledFor || ''));
        return Boolean(item?.status === 'scheduled' && Number.isFinite(scheduledMs) && scheduledMs <= Date.now());
    }

    private truncateEngagementTitle(value: string): string {
        const compact = String(value || '').replace(/\s+/g, ' ').trim();
        if (!compact) return 'Message';
        return compact.length <= 72 ? compact : `${compact.slice(0, 69).trimEnd()}...`;
    }

    private defaultEngagementTimezone(): string {
        try {
            return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Oslo';
        } catch {
            return 'Europe/Oslo';
        }
    }

    private normaliseEngagementTimezone(value: string | null | undefined): string | null {
        const timezone = String(value || '').trim();
        if (!timezone) return null;
        try {
            new Intl.DateTimeFormat('en-US', {timeZone: timezone}).format(new Date(0));
            return timezone;
        } catch {
            return null;
        }
    }

    private normaliseScheduleLocalDate(value: string | null | undefined): string | null {
        const trimmed = String(value || '').trim();
        if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return null;
        const date = new Date(`${trimmed}T00:00:00.000Z`);
        if (Number.isNaN(date.valueOf())) return null;
        return date.toISOString().slice(0, 10) === trimmed ? trimmed : null;
    }

    private normaliseScheduleLocalTime(value: string | null | undefined): string | null {
        const trimmed = String(value || '').trim();
        const match = /^(\d{2}):(\d{2})$/.exec(trimmed);
        if (!match) return null;
        const hour = Number.parseInt(match[1], 10);
        const minute = Number.parseInt(match[2], 10);
        if (!Number.isInteger(hour) || !Number.isInteger(minute)) return null;
        if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
        return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
    }

    private getZonedParts(date: Date, timezone: string): {
        year: number;
        month: number;
        day: number;
        hour: number;
        minute: number;
        second: number;
    } {
        const formatter = new Intl.DateTimeFormat('en-CA', {
            timeZone: timezone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hourCycle: 'h23',
        });
        const parts: Record<string, number> = {};
        formatter.formatToParts(date).forEach((part) => {
            if (part.type !== 'literal') {
                parts[part.type] = Number(part.value);
            }
        });
        return {
            year: parts['year'],
            month: parts['month'],
            day: parts['day'],
            hour: parts['hour'],
            minute: parts['minute'],
            second: parts['second'],
        };
    }

    private localPartsToComparableMs(parts: {
        year: number;
        month: number;
        day: number;
        hour: number;
        minute: number;
        second: number;
    }): number {
        return Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second || 0);
    }

    private resolveLocalDateTime(localDate: string, localTime: string, timezone: string): EngagementScheduleResolution {
        const safeTimezone = this.normaliseEngagementTimezone(timezone);
        const safeDate = this.normaliseScheduleLocalDate(localDate);
        const safeTime = this.normaliseScheduleLocalTime(localTime);
        if (!safeTimezone || !safeDate || !safeTime) {
            return {scheduledFor: null, error: 'invalid'};
        }

        const [year, month, day] = safeDate.split('-').map((part) => Number.parseInt(part, 10));
        const [hour, minute] = safeTime.split(':').map((part) => Number.parseInt(part, 10));
        const target = {year, month, day, hour, minute, second: 0};
        const targetComparable = this.localPartsToComparableMs(target);
        let utcMs = targetComparable;

        for (let attempt = 0; attempt < 6; attempt += 1) {
            const zoned = this.getZonedParts(new Date(utcMs), safeTimezone);
            const deltaMs = this.localPartsToComparableMs(zoned) - targetComparable;
            if (deltaMs === 0) {
                const matches = this.findMatchingUtcInstants(targetComparable, safeTimezone, utcMs);
                if (matches.length > 1) {
                    return {scheduledFor: null, error: 'ambiguous'};
                }
                return {scheduledFor: new Date(matches[0] ?? utcMs).toISOString(), error: null};
            }
            utcMs -= deltaMs;
        }

        const zoned = this.getZonedParts(new Date(utcMs), safeTimezone);
        if (this.localPartsToComparableMs(zoned) !== targetComparable) {
            return {scheduledFor: null, error: 'invalid'};
        }
        const matches = this.findMatchingUtcInstants(targetComparable, safeTimezone, utcMs);
        if (matches.length > 1) {
            return {scheduledFor: null, error: 'ambiguous'};
        }
        return {scheduledFor: new Date(matches[0] ?? utcMs).toISOString(), error: null};
    }

    private findMatchingUtcInstants(targetComparable: number, timezone: string, seedUtcMs: number): number[] {
        const matches = new Set<number>();
        for (let offsetMinutes = -180; offsetMinutes <= 180; offsetMinutes += 15) {
            const candidateMs = seedUtcMs + offsetMinutes * 60 * 1000;
            const zoned = this.getZonedParts(new Date(candidateMs), timezone);
            if (this.localPartsToComparableMs(zoned) === targetComparable) {
                matches.add(candidateMs);
            }
        }
        return Array.from(matches).sort((a, b) => a - b);
    }

    private formatScheduleLocalDate(value: string | null | undefined): string | null {
        if (!value) return null;
        const date = new Date(value);
        if (Number.isNaN(date.valueOf())) return null;
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    private formatScheduleLocalTime(value: string | null | undefined): string | null {
        if (!value) return null;
        const date = new Date(value);
        if (Number.isNaN(date.valueOf())) return null;
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${hours}:${minutes}`;
    }
}
